// =============================================================================
// Origin UI — src/pages/admin/ModuleSettingsAdminTab.tsx
// Admin Config tab for toggling module visibility.
// =============================================================================

import { useState, useEffect } from 'react';
import { api } from '@/services/apiService';
import { invalidateModuleFlags } from '@/hooks/useModuleFlags';
import styles from './admin.module.css';

interface ModuleSetting {
  moduleSettingId: string;
  moduleKey: string;
  displayName: string;
  description: string | null;
  isEnabled: boolean;
  sortOrder: number;
  iconName: string | null;
}

export default function ModuleSettingsAdminTab() {
  const [modules, setModules] = useState<ModuleSetting[]>([]);
  const [loading, setLoading] = useState(true);
  const [toggling, setToggling] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    loadModules();
  }, []);

  async function loadModules() {
    try {
      const data = await api.get<ModuleSetting[]>('/api/admin/modules');
      setModules(data);
    } catch {
      setError('Failed to load module settings');
    } finally {
      setLoading(false);
    }
  }

  async function handleToggle(mod: ModuleSetting) {
    setToggling(mod.moduleSettingId);
    setError(null);
    try {
      const updated = await api.put<ModuleSetting>(
        `/api/admin/modules/${mod.moduleSettingId}`,
        { isEnabled: !mod.isEnabled }
      );
      setModules(prev =>
        prev.map(m => m.moduleSettingId === updated.moduleSettingId ? updated : m)
      );
      // Invalidate the cached flags so sidebar/routes pick up the change
      invalidateModuleFlags();
    } catch {
      setError(`Failed to update ${mod.displayName}`);
    } finally {
      setToggling(null);
    }
  }

  if (loading) return <div className={styles.loading}>Loading module settings...</div>;

  return (
    <div className={styles.tabContent}>
      <div className={styles.tabHeader}>
        <h3>Module Visibility</h3>
        <p className={styles.tabDescription}>
          Toggle modules on or off. Disabled modules are hidden from the sidebar, 
          home screen, and all routes. Changes take effect immediately — no restart needed.
        </p>
      </div>

      {error && <div className={styles.errorBanner}>{error}</div>}

      <div className={styles.moduleList}>
        {modules.map(mod => (
          <div key={mod.moduleSettingId} className={styles.moduleRow}>
            <div className={styles.moduleInfo}>
              {mod.iconName && <span className={styles.moduleIcon}>{mod.iconName}</span>}
              <div>
                <div className={styles.moduleName}>{mod.displayName}</div>
                {mod.description && (
                  <div className={styles.moduleDescription}>{mod.description}</div>
                )}
              </div>
            </div>
            <label className={styles.toggleSwitch}>
              <input
                type="checkbox"
                checked={mod.isEnabled}
                disabled={toggling === mod.moduleSettingId}
                onChange={() => handleToggle(mod)}
              />
              <span className={styles.toggleSlider}></span>
              <span className={styles.toggleLabel}>
                {mod.isEnabled ? 'Visible' : 'Hidden'}
              </span>
            </label>
          </div>
        ))}
      </div>
    </div>
  );
}
